import { useCallback, useState } from 'react';

interface Toast {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  duration?: number;
}

export const useAuthToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: Toast['type'] = 'info', duration = 3000) => {
    const id = Math.random().toString(36).substring(2, 15);
    const newToast: Toast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto remove after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, duration + 300); // Add 300ms for fade out animation
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showAuthSuccess = useCallback((message = 'Authentication successful') => {
    showToast(message, 'success', 2000);
  }, [showToast]);

  const showAuthWarning = useCallback((message = 'Session refreshed') => {
    showToast(message, 'warning', 2000);
  }, [showToast]);

  const showAuthError = useCallback((message = 'Authentication error') => {
    showToast(message, 'error', 4000);
  }, [showToast]);

  const showAuthInfo = useCallback((message = 'Checking authentication...') => {
    showToast(message, 'info', 2000);
  }, [showToast]);

  return {
    toasts,
    showToast,
    removeToast,
    showAuthSuccess,
    showAuthWarning,
    showAuthError,
    showAuthInfo
  };
};